import { useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { MoisAnneeFilter, anneesDepuis, filtreParams } from "@/components/MoisAnneeFilter";
import { api, errMsg, money } from "@/lib/api";

const Th = ({ children, right }) => <th className={`py-2 px-3 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>;

const Resultats = ({ data }) => (
  <Panel title={`${data.nb} opération${data.nb > 1 ? "s" : ""} trouvée${data.nb > 1 ? "s" : ""}`} testId="recherche-resultats"
    action={<span className="text-xs font-mono text-slate-600" data-testid="recherche-totaux">Crédits {money(data.total_credit)} · Débits {money(data.total_debit)}</span>}>
    {data.nb === 0 ? <p className="text-sm text-slate-500" data-testid="recherche-vide">Aucune opération ne correspond.</p> : (
      <div className="overflow-x-auto -mx-5">
        <SmartTable id="recherche" className="w-full text-sm min-w-[640px]">
          <thead><tr><Th>Date</Th><Th>Description</Th><Th>Logement</Th><Th right>Débit</Th><Th right>Crédit</Th><Th right>Solde</Th></tr></thead>
          <tbody>{data.resultats.map((t) => (
            <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`recherche-row-${t.id}`}>
              <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{t.date}</td>
              <td className="py-2 px-3">{t.description}</td>
              <td className="py-2 px-3 text-xs text-slate-500">{t.logement || "—"}</td>
              <td className="py-2 px-3 text-right font-mono text-red-700">{t.debit ? money(t.debit) : ""}</td>
              <td className="py-2 px-3 text-right font-mono text-emerald-700">{t.credit ? money(t.credit) : ""}</td>
              <td className="py-2 px-3 text-right font-mono text-slate-500">{t.solde != null ? money(t.solde) : ""}</td>
            </tr>))}
          </tbody>
        </SmartTable>
      </div>
    )}
  </Panel>
);

export default function Recherche() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtre, setFiltre] = useState({ annee: "all", mois: "all" });
  const [annees, setAnnees] = useState([]);
  useEffect(() => { api.get("/mois").then((r) => setAnnees(anneesDepuis(r.data))).catch(() => {}); }, []);
  const chercher = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try { setData((await api.get("/transactions/recherche", { params: { q: q.trim(), ...filtreParams(filtre) } })).data); }
    catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div data-testid="recherche-page">
      <PageHeader eyebrow="Relevés Desjardins" title="Rechercher une opération" />
      <form onSubmit={chercher} className="flex flex-col lg:flex-row gap-2 mb-6 max-w-4xl">
        <MoisAnneeFilter value={filtre} onChange={setFiltre} annees={annees} toutes testId="recherche-filtre" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Un montant (ex. 270,04) ou un mot (ex. HYDRO)" className="h-11" data-testid="recherche-input" />
        <Button type="submit" disabled={loading || !q.trim()} className="h-11 bg-emerald-700 hover:bg-emerald-800" data-testid="recherche-button">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}Rechercher
        </Button>
      </form>
      {data && <Resultats data={data} />}
    </div>
  );
}
